import { useEffect, useMemo, useState } from "react";
import { ComboBoxInput } from "./comboBoxInput.tsx";
import { ISO_DATA, findIsoCurrency } from "../../util/isoCurrency.ts";

interface CurrencyPickerProps {
  /** 3-letter ISO 4217 code (e.g. "EUR"). Empty string when unset. */
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Searchable currency picker. Type-ahead over the full ISO 4217 catalog
 * (~178 entries) — user can match by code ("EUR"), name ("Euro"), or any
 * substring the browser's native datalist accepts.
 *
 * Internal draft text lets the user freely edit. We commit a new `value`
 * to the parent only when the typed text resolves to a known ISO code; on
 * blur with an unrecognised value the input snaps back to the prior code's
 * canonical label.
 */
export function CurrencyPicker({
  value, onChange, disabled, placeholder, className, style,
}: CurrencyPickerProps) {
  const options = useMemo(
    () => ISO_DATA.map(e => `${e.code} — ${e.currency}`),
    [],
  );

  const labelFor = (code: string): string => {
    const e = findIsoCurrency(code);
    return e ? `${e.code} — ${e.currency}` : code;
  };

  const [text, setText] = useState<string>(() => labelFor(value));

  useEffect(() => { setText(labelFor(value)); }, [value]);

  function tryResolve(raw: string): string | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    // Pick the leading token before the "—" if present.
    const head = trimmed.split("—")[0]!.trim().toUpperCase();
    if (findIsoCurrency(head)) return head;
    // Also accept exact code typed anywhere.
    const m = trimmed.match(/\b([A-Z]{3})\b/i);
    if (m) {
      const e = findIsoCurrency(m[1]!);
      if (e) return e.code;
    }
    return null;
  }

  return (
    <ComboBoxInput
      value={text}
      readOnly={disabled}
      placeholder={placeholder ?? "EUR — Euro"}
      ariaLabel="Currency"
      className={className}
      style={{ width: "100%", ...style }}
      options={options}
      onChange={(raw) => {
        setText(raw);
        const code = tryResolve(raw);
        if (code && code !== value) onChange(code);
      }}
    />
  );
}
